import React, { useEffect, useState, useCallback, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Table } from 'react-bootstrap'
import userApi from '../../api/UserApi'
import { Toolbar, ToolbarButton } from '../common/core/Toolbar'
import { FaSync, FaSearch } from 'react-icons/fa'
import DropDownSelect from '../common/core/DropDownSelect'
import { Col, Row } from 'reactstrap'
import { Form } from 'react-bootstrap'
import * as commonSelectors from '../common/selectors'
import { commonActions } from '../../actions/commonActions'
import { logActions } from '../../actions/logActions'
import { isNullOrEmpty } from '../../shared/scripts'
import Toggle from '../common/core/toggle/Toggle'
import './logViewer.css'

const LogViewer = () => {
    const rowHeight = 32;
    const headerHeight = 30;
    const logPercent = 0.52;
    const logNamesPercent = 0.1;
    const logDetailPercent = 0.15;
    const [logs, setLogs] = useState([]);
    const [loaded, setLoaded] = useState(false);

    const [windowHeight, setWindowHeight] = useState(window.innerHeight);
    const [elementsHeight, setElementsHeight] = useState({
        logDetail: `${Math.floor(windowHeight * logDetailPercent)}px`,
        logNames: `${Math.floor(windowHeight * logNamesPercent)}px`,
        log: `${Math.floor(windowHeight * logPercent)}px`,
    });

    const [hasRight, setHasRight] = useState(false);
    const [files, setFiles] = useState({});
    const [detail, setDetail] = useState(null);
    const [detailId, setDetailId] = useState(null);
    const [minDateTime, setMinDateTime] = useState(null);
    const [filterApplied, setFilterApplied] = useState(false);
    const [isFilterEnabled, setIsFilterEnabled] = useState(false);

    const [tenantFilter, setTenantFilter] = useState(null);
    const [triggerFilter, setTriggerFilter] = useState(null);
    const [messageFilter, setMessageFilter] = useState(null);
    const [eventTypeFilter, setEventTypeFilter] = useState(null);

    const isAuthorizedSelector = useSelector(commonSelectors.isAuthorized);
    const rightsSelector = useSelector(commonSelectors.userRights);
    const dispatch = useDispatch();
    const filterFormRef = useRef(null);

    useEffect(() => {
        const handleWindowResize = () => setWindowHeight(window.innerHeight);
        window.addEventListener('resize', handleWindowResize);

        return () => {
            window.removeEventListener('resize', handleWindowResize);
        };
    }, []);

    const getHeightPxByCount = useCallback((rowsCount = 1, maxHeightPercentage = logNamesPercent, add = 0) => {
        const currentHeight = (rowsCount * rowHeight) + headerHeight + 10;
        const maxHeight = Math.floor(windowHeight * maxHeightPercentage) + add;

        const height = currentHeight > maxHeight
            ? maxHeight
            : currentHeight;

        return `${height}px`;
    }, [windowHeight]);

    const getLogHeight = useCallback(() => {
        const filterPanelHeight = isFilterEnabled ? 150 : 0;

        return getHeightPxByCount(logs.length, logPercent, -filterPanelHeight);
    }, [logs, isFilterEnabled, getHeightPxByCount]);

    const getLogNamesHeight = useCallback(() => {
        if (logs.length === 0) {
            return 0;
        }

        return getHeightPxByCount(Object.entries(files).length);
    }, [logs, files, getHeightPxByCount]);

    const getLogDetailHeight = useCallback(() => {
        return `${Math.floor(windowHeight * logDetailPercent)}px`;
    }, [windowHeight]);

    useEffect(() => {
        setElementsHeight({
            logDetail: getLogDetailHeight(),
            logNames: getLogNamesHeight(),
            log: getLogHeight(),
        });
    }, [getLogDetailHeight, getLogHeight, getLogNamesHeight]);


    const setData = useCallback((data, _filterApplied, loadMore) => {
        setLoaded(true);

        if (data.logs.length === 0 && !loadMore) {
            setLogs([]);
            setDetail(null);
            setDetailId(null);
            setMinDateTime(null);
            setFiles({});
            setFilterApplied(_filterApplied);

            return;
        }

        const _detail = getDetailInner();
        const _files = getFilesInner();
        const _logs = loadMore ? logs.concat(data.logs) : data.logs;

        setDetail(_detail);
        setDetailId(_detail.id);
        setFiles(_files);
        setLogs(_logs);
        setMinDateTime(data.minDateTime);
        setFilterApplied(_filterApplied);

        function getDetailInner() {
            if (detail != null && filterApplied == _filterApplied && !loadMore) {
                const idx = data.logs.findIndex(x => x.dateTime == detail.dateTime);
                if (idx !== -1) {
                    return data.logs[idx];
                }
            }

            return loadMore ? { ...detail } : data.logs[0];
        }

        function getFilesInner() {
            const _files = loadMore
                ? {
                    ...files
                }
                : {};
            for (var i = 1; i <= data.logFiles.length; i++) {
                const file = data.logFiles[i - 1];

                if (files[file.fileName] != null && !loadMore) {
                    _files[file.fileName] = {
                        ...file,
                        id: i,
                        color: files[file.fileName].color,
                    };
                } else {
                    _files[file.fileName] = {
                        ...file,
                        id: i,
                        color: getColorInner(),
                    };
                }
            }

            return _files;
        }

        function getColorInner() {
            function rand(min, max) {
                return parseInt(Math.random() * (max - min + 1), 10) + min;
            }
            const h = rand(1, 360);   //  color hue between 1 and 360
            const s = rand(30, 100);  //  saturation 30-100%
            const l = rand(30, 70);   //  lightness 30-70%

            return 'hsl(' + h + ',' + s + '%,' + l + '%)';
        }
    }, [detail, files, logs, filterApplied]);

    const resetFilters = () => {
        setTenantFilter(null);
        setTriggerFilter(null);
        setMessageFilter(null);
        setEventTypeFilter(null);
    };

    const handleRefresh = useCallback(() => {
        dispatch(
            logActions.getLogs({
                TenantFilter: null,
                TriggerFilter: null,
                MessageFilter: null,
                EventTypeFilter: null,
            })
        ).then(response => {
            resetFilters();
            setIsFilterEnabled(false);
            setData(response.data, false);
        });
    }, [dispatch, setData]);

    useEffect(() => {
        const _hasRight = rightsSelector?.fullControl ?? false;
        const _isAuthorized = isAuthorizedSelector ?? false;

        if (!_isAuthorized) {
            userApi.login();
        } else if (_isAuthorized && !_hasRight) {
            commonActions.history.accessForbidden();
        } else if (!loaded) {    //  _isAuthorized && hasRight            
            handleRefresh();
        }

        setHasRight(_hasRight);
    }, [isAuthorizedSelector, rightsSelector, loaded, handleRefresh, dispatch]);


    const handleSearchByFilterClick = useCallback(() => {
        dispatch(
            logActions.getLogs({
                TenantFilter: tenantFilter,
                TriggerFilter: triggerFilter,
                MessageFilter: messageFilter,
                EventTypeFilter: eventTypeFilter,
            })
        ).then(response => {
            setData(response.data, true);
        });
    }, [dispatch, tenantFilter, triggerFilter, messageFilter, eventTypeFilter, setData]);

    const handleScroll = useCallback(e => {
        // load more by scroll position
        const bottom = (e.target.scrollHeight - e.target.scrollTop) === e.target.clientHeight;
        if (bottom && minDateTime != null) {
            const _filter = filterApplied
                ? {
                    TenantFilter: tenantFilter,
                    TriggerFilter: triggerFilter,
                    MessageFilter: messageFilter,
                    EventTypeFilter: eventTypeFilter,
                    toDateTime: minDateTime,
                }
                : {
                    TenantFilter: null,
                    TriggerFilter: null,
                    MessageFilter: null,
                    EventTypeFilter: null,
                    toDateTime: minDateTime,
                };

            dispatch(
                logActions.getLogs(_filter)
            ).then(response => {
                setData(response.data, filterApplied, true);
            });
        }
    }, [dispatch, filterApplied, minDateTime, tenantFilter, triggerFilter, messageFilter, eventTypeFilter, setData]);


    const handleFilterUpdate = (field, value) => {
        const getValueOrNull = value => isNullOrEmpty(value) ? null : value;

        switch (field) {
            case 'TenantFilter':
                setTenantFilter(
                    getValueOrNull(value)
                );
                break;
            case 'TriggerFilter':
                setTriggerFilter(
                    getValueOrNull(value)
                );
                break;
            case 'MessageFilter':
                setMessageFilter(
                    getValueOrNull(value)
                );
                break;
            case 'EventTypeFilter':
                setEventTypeFilter(
                    getValueOrNull(value)
                );
                break;
            default:
                console.log('Unknown filter field: ' + field);
                return;
        }
    };

    return (hasRight
        ? <div className='bs-log-viewer-root'>
            <Toolbar className="toolbar-desc">
                <div className="toolbar-title">
                    <span>
                        Log Viewer
                    </span>
                </div>
                {filterApplied
                    ? <ToolbarButton
                        onClick={() => handleRefresh()}
                        text={"Clear Filter"}
                        icon={<FaSync />}
                        color={"orange"}
                    />
                    : <ToolbarButton
                        onClick={() => handleRefresh()}
                        text={"Refresh"}
                        icon={<FaSync />}
                    />
                }
                <ToolbarButton
                    onClick={() => handleSearchByFilterClick()}
                    text={"Search By Filter"}
                    icon={<FaSearch />}
                    color={isFilterEnabled ? "green" : "gray"}
                    disabled={!isFilterEnabled}
                />
            </Toolbar>
            <Toggle
                className="filter-toggle"
                checked={isFilterEnabled}
                onChange={val => {
                    setIsFilterEnabled(val)
                }}
                label="Filtering Options"
            />
            <form
                className={`f-log-filter filter-form${isFilterEnabled ? '' : ' hidden'}`}
                ref={filterFormRef}
            >
                <Row>
                    <Form.Group
                        style={{ marginBottom: "5px" }}
                        as={Col}
                        controlId="RoundingMode"
                    >
                        <Form.Label
                            style={{ fontWeight: '500' }}
                        >
                            Type
                        </Form.Label>
                        <DropDownSelect
                            name="type"
                            className="drop-down-param"
                            defaultText="Select Type..."
                            optionsList={
                                [
                                    { label: "Info", value: "Info" },
                                    { label: "Warn", value: "Warn" },
                                    { label: "Error", value: "Error" },
                                    { label: "Debug", value: "Debug" },
                                    { label: "Fatal", value: "Fatal" },
                                ]
                            }
                            fullWidth={true}
                            isClearable={true}
                            value={eventTypeFilter}
                            onSelect={str => handleFilterUpdate('EventTypeFilter', str)}
                        />
                    </Form.Group>
                    <Form.Group
                        style={{ marginBottom: "5px" }}
                        as={Col}
                        controlId="TenantFilter"
                    >
                        <Form.Label style={{
                            fontWeight: '500'
                        }}>
                            Tenant Contains
                        </Form.Label>
                        <Form.Control
                            className="global-input adj-input desc-param"
                            type="text"
                            name="TenantFilter"
                            onChange={e => handleFilterUpdate(e.target.name, e.target.value)}
                            value={tenantFilter ?? ''}
                            autoComplete="off"
                        />
                    </Form.Group>
                    <Form.Group
                        style={{ marginBottom: "5px" }}
                        as={Col}
                        controlId="TriggerFilter"
                    >
                        <Form.Label style={{
                            fontWeight: '500'
                        }}>
                            Trigger Contains
                        </Form.Label>
                        <Form.Control
                            className="global-input adj-input desc-param"
                            type="text"
                            name="TriggerFilter"
                            onChange={e => handleFilterUpdate(e.target.name, e.target.value)}
                            value={triggerFilter ?? ''}
                            autoComplete="off"
                        />
                    </Form.Group>
                </Row>
                <Row style={{
                    marginTop: '0px'
                }}>
                    <Form.Group
                        style={{
                            marginBottom: "1rem"
                        }}
                        as={Col}
                        controlId="MessageFilter"
                    >
                        <Form.Label style={{
                            fontWeight: '500'
                        }}>
                            Message Contains
                        </Form.Label>
                        <Form.Control
                            className="global-input adj-input desc-param"
                            type="text"
                            name="MessageFilter"
                            onChange={e => handleFilterUpdate(e.target.name, e.target.value)}
                            value={messageFilter ?? ''}
                            autoComplete="off"
                        />
                    </Form.Group>
                </Row>
            </form>
            {logs.length > 0 &&
                <div className='logs-wrapper'>
                    <div
                        className='log-names'
                        style={{
                            height: elementsHeight.logNames,
                        }}
                    >
                        <Table
                            id="log-table"
                            striped
                            bordered
                            hover
                            size="sm"
                        >
                            <thead>
                                <tr>
                                    <th className="th-lf"></th>
                                    <th style={{ width: '260px' }}>LogFileName</th>
                                    <th style={{ width: '240px' }}>LastWriteTime</th>
                                    <th style={{ width: '110px' }}>Informations</th>
                                    <th style={{ width: '110px' }}>Warnings</th>
                                    <th style={{ width: '110px' }}>Errors</th>
                                </tr>
                            </thead>
                            <tbody
                                style={{
                                    fontSize: 12,
                                }}
                            >
                                {
                                    renderLogFilesRows()
                                }
                            </tbody>
                        </Table>
                    </div>
                    <div
                        id="log-div"
                        onScroll={handleScroll}
                        style={{
                            height: elementsHeight.log,
                        }}
                    >
                        <Table
                            id="log-table"
                            striped
                            bordered
                            hover
                            size="sm"
                        >
                            <thead>
                                <tr>
                                    <th
                                        className="th-lf"
                                        style={{ width: '20px' }}
                                    />
                                    <th style={{ width: '100px' }}>Time</th>
                                    <th style={{ width: '60px' }}>Type</th>
                                    <th style={{ width: '50px' }}>Tenant</th>
                                    <th style={{ width: '50px' }}>Trigger</th>
                                    <th style={{ maxWidth: '400px' }}>Message</th>
                                </tr>
                            </thead>
                            <tbody style={{ fontSize: 12 }}>
                                {
                                    renderLogsRows()
                                }
                            </tbody>
                        </Table>
                    </div>
                    {detail != null &&
                        <textarea
                            style={{
                                height: elementsHeight.logDetail,
                            }}
                            id="pre-log-detail"
                            value={detail.details}
                            readOnly
                        />
                    }
                </div>
            }
            {logs.length === 0 && loaded &&
                <div style={{
                    marginTop: "15px",
                    color: "orange"
                }}>
                    <span>No logs found ...</span>
                </div>
            }
        </div>
        : <div />
    );

    function renderLogsRows() {
        if (logs.length != 0) {
            return logs.map((params, index) => {
                const {
                    dateTime,
                    header,
                    tenant,
                    type,
                    logFileName,
                    trigger,
                } = params;
                const _file = files[logFileName];
                return (
                    <tr
                        className={`log-tr${params.id === detailId ? ' selected-log' : ''}`}
                        key={index}
                        onClick={() => {
                            setDetail(params);
                            setDetailId(params.id);
                        }}
                    >
                        <td style={{ align: 'center', fontWeight: 'bold', color: _file?.color ?? 'black' }}>{_file?.id ?? 'unc'} </td>
                        <td style={{ align: 'center', whiteSpace: 'nowrap' }}>{dateTime.replace(new RegExp("\\d{4}-\\d{2}-\\d{2}T", "gm"), "")} </td>
                        <td style={{ align: 'center', fontWeight: 'bold', color: type === 'Info' ? 'green' : type === 'Error' ? 'red' : 'orange' }}>{type.toUpperCase()} </td>
                        <td style={{ align: 'center', fontWeight: 'bold' }}>{tenant} </td>
                        <td style={{ align: 'center' }}>{trigger} </td>
                        <td style={{ align: 'center', whiteSpace: 'nowrap' }}>{header} </td>
                    </tr>
                )
            })
        }
    }

    function renderLogFilesRows() {
        if (files != {}) {
            return Object
                .entries(files)
                .map(([key, value], index) => {
                    return (
                        <tr
                            key={index}
                            className='log-tr'
                        >
                            <td style={{ align: 'center', fontWeight: 'bold', color: value.color }}>{value.id} </td>
                            <td style={{ align: 'center', fontWeight: 'bold' }}>{key} </td>
                            <td style={{ align: 'center', whiteSpace: 'nowrap' }}>{value.lastWriteTime} </td>
                            <td style={{ align: 'center', whiteSpace: 'nowrap', color: 'green', fontWeight: 'bold' }}>{value.informationCount} </td>
                            <td style={{ align: 'center', whiteSpace: 'nowrap', color: 'orange', fontWeight: 'bold' }}>{value.warningCount} </td>
                            <td style={{ align: 'center', whiteSpace: 'nowrap', color: 'red', fontWeight: 'bold' }}>{value.errorCount} </td>
                        </tr>
                    );
                });
        }
    }
}

export default LogViewer;